// Client-side inactivity tracking.
//
// The backend's login token may stay valid for a long time (or indefinitely) —
// that's controlled by the separate backend repo, not this frontend. This
// module adds an independent, frontend-only rule on top of that: if the
// person hasn't touched the app in a while, treat them as logged out on
// their next visit, even if their token would technically still work.
//
// Adjust INACTIVITY_LIMIT_MS to change how long "a while" is.

const LAST_ACTIVE_KEY = "wtwr_last_active";
export const INACTIVITY_LIMIT_MS = 24 * 60 * 60 * 1000; // 24 hours

// Avoid hammering localStorage on every mousemove/keydown.
const WRITE_THROTTLE_MS = 60 * 1000; // 1 minute
let lastWrite = 0;

export const recordActivity = () => {
  const now = Date.now();
  if (now - lastWrite < WRITE_THROTTLE_MS) {
    return;
  }
  lastWrite = now;
  try {
    localStorage.setItem(LAST_ACTIVE_KEY, String(now));
  } catch {
    // localStorage unavailable — silently skip, not critical
  }
};

export const getLastActive = () => {
  try {
    const raw = localStorage.getItem(LAST_ACTIVE_KEY);
    return raw ? Number(raw) : null;
  } catch {
    return null;
  }
};

export const hasExceededInactivityLimit = () => {
  const lastActive = getLastActive();
  if (!lastActive) {
    // No record yet (e.g. first visit) — nothing to expire.
    return false;
  }
  return Date.now() - lastActive > INACTIVITY_LIMIT_MS;
};

export const clearActivity = () => {
  try {
    localStorage.removeItem(LAST_ACTIVE_KEY);
  } catch {
    // ignore
  }
};

// Attaches listeners for common user-interaction events and calls
// recordActivity() (throttled) whenever one fires. Returns a cleanup
// function to remove the listeners.
export const startActivityTracking = () => {
  const events = ["mousemove", "keydown", "click", "touchstart", "scroll"];
  events.forEach((event) => {
    window.addEventListener(event, recordActivity, { passive: true });
  });
  recordActivity();

  return () => {
    events.forEach((event) => {
      window.removeEventListener(event, recordActivity);
    });
  };
};
